import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
  AfterViewInit,
  SimpleChanges
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import * as noUiSlider from '../utilities/slider/nouislider.min';

export interface NouiFormatter {
  to(value: number): string;
  from(value: string): number;
}

export class DefaultFormatter implements NouiFormatter {
  to(value: number): string {
    // formatting with http://stackoverflow.com/a/26463364/478584
    return String(parseFloat(parseFloat(String(value)).toFixed(2)));
  }
  from(value: string): number {
    return parseFloat(value);
  }
}

@Component({
  selector: 'axis-slider',
  encapsulation: ViewEncapsulation.None,
  template: '<div [attr.disabled]="disabled ? true : undefined"></div>',
  host: {
    '[class.axis-slider]': 'true'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true
    }
  ]
})
export class SliderComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit {
  public slider: any;
  public handles: any[];
  @Input() public disabled: boolean; // tslint:disable-line
  @Input() public behaviour: string;
  @Input() public connect: boolean[];
  @Input() public direction = 'ltr';
  @Input() public label = 'Slider';
  @Input() public limit: number;
  @Input() public min: number;
  @Input() public max: number;
  @Input() public step: number;
  @Input() public format: NouiFormatter;
  @Input() public pageSteps: number;
  @Input() public config: any = {};
  @Input() public ngModel: number | number[];
  @Input() public keyboard: boolean;
  @Input() public onKeydown: any;
  @Input() public formControl: FormControl;
  @Input() public tooltips: Array<any>;
  @Input() public valuetext: Array<any>;
  @Output() public change: EventEmitter<any> = new EventEmitter(true);
  @Output() public update: EventEmitter<any> = new EventEmitter(true);
  @Output() public slide: EventEmitter<any> = new EventEmitter(true);
  @Output() public set: EventEmitter<any> = new EventEmitter(true);
  @Output() public start: EventEmitter<any> = new EventEmitter(true);
  @Output() public end: EventEmitter<any> = new EventEmitter(true);

  // @HostBinding('class.axis-slider') axisSlider = true;
  @HostBinding('class.no-marker-divider') @Input() hideMarkDivider = false;
  @HostBinding('class.no-tooltip-label') @Input() hideToolTipLabel = false;
  @HostBinding('class.no-marker-divider-values') @Input() hideMarkDividerValues = false;



  private value: any;
  private onChange: any = Function.prototype;
  private onTouched: any = Function.prototype;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {

    const inputsConfig = JSON.parse(
      JSON.stringify({
        behaviour: this.behaviour,
        connect: this.connect,
        direction: this.direction,
        // keyboard: this.keyboard,
        keyboard: true,
        label: this.label,
        limit: this.limit,
        onKeydown: this.onKeydown,
        pageSteps: this.pageSteps,
        range: this.config.range || { min: this.min, max: this.max },
        start:
          this.formControl !== undefined
            ? this.formControl.value
            : this.ngModel,
        // step: this.step,
        // accessibility defines a step as 1; use step for label
        step: 1,
        stepLabel: this.pageSteps,
        tooltips: this.tooltips,
        valuetext: this.valuetext,
        // css
        cssPrefix: 'axis-',
        cssClasses: {
          active: 'active',
          background: 'background',
          base: 'base',
          connect: 'connect',
          connects: 'connects',
          drag: 'state-drag',
          draggable: 'draggable',
          handle: 'handle',
          handleLower: 'handle-lower',
          handleUpper: 'handle-upper',
          horizontal: 'horizontal',
          ltr: 'ltr',
          origin: 'origin',
          pips: 'pips',
          pipsHorizontal: 'pips-horizontal',
          pipsVertical: 'pips-vertical',
          marker: 'marker',
          markerHorizontal: 'marker-horizontal',
          markerLarge: 'marker-large',
          markerNormal: 'marker-normal',
          markerSub: 'marker-sub',
          markerVertical: 'marker-vertical',
          rtl: 'rtl',
          tap: 'state-tap',
          target: 'target',
          tooltip: 'slider-tooltip',
          value: 'value',
          valueHorizontal: 'value-horizontal',
          valueLarge: 'value-large',
          valueNormal: 'value-normal',
          valueSub: 'value-sub',
          valueVertical: 'value-vertical',
          vertical: 'vertical',
        }
      })
    );

    if (this.config) {
      this.config['valuetext'] = this.config['valuetext'] ? this.config['valuetext'] : [];
      this.valuetext = this.config['valuetext'];
      this.config['connect'] = this.config['connect'] ? this.config['connect'] : [true , false];
      this.config['label'] = this.config['label'] ? this.config['label'] : 'Slider';
      this.label = this.config['label'];
      this.config['stepLabel'] = this.config['pageSteps'] ? this.config['pageSteps'] : 10;
    }

    inputsConfig.format =
      this.format || this.config.format || new DefaultFormatter();
    this.slider = noUiSlider.create(
      this.el.nativeElement.querySelector('div'),
      Object.assign(this.config, inputsConfig)
    );

    const axisPipsRef = this.el.nativeElement.querySelectorAll('.axis-pips .axis-value');
    if (axisPipsRef.length) {
      this.renderer.addClass(axisPipsRef[0], 'first');
      this.renderer.addClass(axisPipsRef[axisPipsRef.length - 1], 'last');
      for (let pipCount = 0; pipCount < axisPipsRef.length; pipCount++) {
        if (pipCount % this.config['stepLabel'] !== 0) {
          this.renderer.addClass(axisPipsRef[pipCount], '_hidden');
        }
      }

    }

    this.handles = [].slice.call(
      this.el.nativeElement.querySelectorAll('.axis-handle')
    );

    if (this.config.keyboard) {
      if (this.config.pageSteps === undefined) {
        this.config.pageSteps = 10;
      }
      for (const handle of this.handles) {
        handle.addEventListener('click', () => {
          handle.focus();
        });
        handle.setAttribute('aria-label', this.label);
        setTimeout(() => {
          if (this.valuetext.length > 0) {
            handle.setAttribute('aria-valuetext', this.valuetext[this.config.start]);
          }
        }, 500);
        handle.setAttribute('tabindex', 0);
        if (this.config.onKeydown === undefined) {
          handle.addEventListener('keydown', this.defaultKeyHandler);
        } else {
          handle.addEventListener('keydown', this.config.onKeydown);
        }
      }
    }
    this.slider.on(
      'set',
      (values: string[], handle: number, unencoded: number[]) => {
        this.eventHandler(this.set, values, handle, unencoded);
        this.setTooltipClass(values);
      }
    );
    this.slider.on(
      'update',
      (values: string[], handle: number, unencoded: number[]) => {
        this.update.emit(this.toValues(values));
        this.setTooltipClass(values);
      }
    );
    this.slider.on(
      'change',
      (values: string[], handle: number, unencoded: number[]) => {
        this.change.emit(this.toValues(values));
      }
    );
    this.slider.on(
      'slide',
      (values: string[], handle: number, unencoded: number[]) => {
        this.eventHandler(this.slide, values, handle, unencoded);
        this.setTooltipClass(values);
      }
    );
    this.slider.on(
      'start',
      (values: string[], handle: number, unencoded: number[]) => {
        this.start.emit(this.toValues(values));
        this.setTooltipClass(values);
      }
    );
    this.slider.on(
      'end',
      (values: string[], handle: number, unencoded: number[]) => {
        this.end.emit(this.toValues(values));
        this.setTooltipClass(values);
      }
    );
  }

  private setTooltipClass = (values) => {
    const axisPipsRef = this.el.nativeElement.querySelectorAll('.axis-slider-tooltip');
    for (let i = 0; i < axisPipsRef.length; i++) {
      this.renderer.removeClass(axisPipsRef[i], 'first');
      this.renderer.removeClass(axisPipsRef[i], 'last');
      if (parseInt(values[i], 10) === parseInt(this.config.range.max, 10)) {
        this.renderer.addClass(axisPipsRef[i], 'last');
      } else if (parseInt(values[i], 10) === parseInt(this.config.range.min, 10)) {
        this.renderer.addClass(axisPipsRef[i], 'first');
      }
    }
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }
    if (this.slider && (changes.min || changes.max || changes.step)) {
      setTimeout(() => {
        this.slider.updateOptions({
          range: {
            min: this.min,
            max: this.max
          },
          // step: this.step
          step: 1
        });
      });
    }
  }

  toValues(values: string[]): any | any[] {
    const v = values.map(this.config.format.from);
    return v.length === 1 ? v[0] : v;
  }

  writeValue(value: any): void {
    if (this.slider) {
      this.slider.set(value);
    }
  }

  registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.renderer.setAttribute(
        this.el.nativeElement.childNodes[0],
        'disabled',
        'true'
      )
      : this.renderer.removeAttribute(
        this.el.nativeElement.childNodes[0],
        'disabled'
      );
  }

  private eventHandler = (
    emitter: EventEmitter<any>,
    values: string[],
    handle: number,
    unencoded: number[]
  ) => {
    const v = this.toValues(values);
    let emitEvents = false;
    if (this.value === undefined) {
      this.value = v;
      return;
    }
    if (Array.isArray(v) && this.value[handle] !== v[handle]) {
      emitEvents = true;
    }
    if (!Array.isArray(v) && this.value !== v) {
      emitEvents = true;
    }
    if (emitEvents) {
      emitter.emit(v);
      this.onChange(v);
    }
    if (Array.isArray(v)) {
      this.value[handle] = v[handle];
    } else {
      this.value = v;
    }
  };

  private defaultKeyHandler = (event: KeyboardEvent) => {
    const index = parseInt((event.target as HTMLElement).getAttribute('data-handle'), 10);
    let newPosition: number | number[];
    let value: number;
    if (Array.isArray(this.value)) {
      value = this.value[index];
    } else {
      value = this.value;
    }

    switch (event.which) {
      case 33: // pageup
        event.preventDefault();
        if (this.config.orientation === 'vertical') {
          newPosition = value - this.config.pageSteps;
        } else {
          newPosition = value + this.config.pageSteps;
        }
        break;
      case 34: // pagedown
        event.preventDefault();
        if (this.config.orientation === 'vertical') {
          newPosition = value + this.config.pageSteps;
        } else {
          newPosition = value - this.config.pageSteps;
        }
        break;
      case 35: // end
        event.preventDefault();
        newPosition = this.config.range.max;
        break;
      case 36: // home
        event.preventDefault();
        newPosition = this.config.range.min;
        break;
      case 39: // arrowright
      case 37: // arrowleft
        event.preventDefault();
        newPosition = value;
        break;
      case 38: // arrowup
      case 40: // arrowdown
        newPosition = value; // ADA support
        break;
      default:
        break;
    }
    // prevent handle from sliding beyond slider range
    if (newPosition > this.config.range.max) {
       newPosition = this.config.range.max;
    } else if (newPosition < this.config.range.min) {
       newPosition = this.config.range.min;
    }

    let newSetting;
    if (Array.isArray(this.value)) { // if two slider handles, prevent handles from crossing each other
      newSetting = [].concat(this.value);

      if (index === 1) {
        if (newPosition < this.value[0]) {
          newPosition = this.value[0];
        }
      } else {
        if (newPosition > this.value[1]) {
          newPosition = this.value[1];
        }
      }
      newSetting[index] = newPosition;
    } else {
      newSetting = newPosition;
    }
    this.slider.set(newSetting);
    if (this.valuetext.length > 0) { // ADA support
      const handle = this.el.nativeElement.querySelectorAll('.axis-handle')[0]; // support one named handle
      handle.setAttribute('aria-valuetext', this.valuetext[newPosition.toString()]);
    }
  };
/**
 *
 * @param disabled
 * true for disabling it
 * false or null for enabling it
 */
  disableSlider(disabled: boolean) {
    this.disabled = disabled ? true : null;
    this.setDisabledState(this.disabled);
  }

  ngAfterViewInit() {    
    setTimeout(() => {
    let isDisabled: any;
    if (!this.disabled) {
        isDisabled = this.el.nativeElement.getAttribute('disabled');
        this.disabled = (isDisabled === 'true' || isDisabled === true) ? true : null;
      }
      if (this.disabled) {
        this.setDisabledState(this.disabled);
      }
      if (isDisabled === 'false' || isDisabled === false) {
          this.renderer.removeAttribute(this.el.nativeElement, 'disabled');
      }
    }, 500);
  }
}
